import { MARKET_TYPE, SEC_TYPE } from 'const/stock'
import { ORDER_TYPE, TIME_IN_FORCE } from 'const/trade'

/**
 * 处理 ib 账户交易合约数据结构。保持和 omnibus 数据结构的一致性。
 * 包括：做多做空保证及、做多做空次数、最小单位等。
 * https://git.tigerbrokers.net/alpha/site-api-docs/-/blob/master/api/contracts.md
 * @param { Object } contract - ib 合约信息结构
 * @param { Object } stock - 股票详情信息
 * @returns { Object } - omnibus 合约信息结构
 */
export default function getLocalFixedContract(contract, stock, isIb) {
  if (isIb) {
    return {
      ...contract,
      canShort: contract.shortable > 0,
      shortableCount: contract.shortable,
      shortFeeRate: contract.feeRate,
      shortInitialMargin: contract.shortMargin,
      shortMaintenanceMargin: contract.shortMargin,
      supportFractionalShare: contract.fractionalShare,
      availableOrderTypes: getAvailableOrderTypes(stock),
      availableTimeInForce: [TIME_IN_FORCE.DAY, TIME_IN_FORCE.GTC],
      tickSizes: getTickSizes(contract.minTicks, stock),
      tradeable: true
    }
  }
  return {
    canShort: false,
    shortableCount: 0,
    shortFeeRate: 0,
    shortInitialMargin: 0,
    shortMaintenanceMargin: 0,
    supportFractionalShare: 0,
    tradeable: false,
    tickSizes: getTickSizes(null, stock),
    availableOrderTypes: [ORDER_TYPE.MKT, ORDER_TYPE.LMT],
    ...contract
  }
}

// 统一处理最小可变数量格式。
function getTickSizes(minTick, stock) {
  // 港股需要自定义数据
  if (stock.market === MARKET_TYPE.HK) {
    return [
      { begin: 0, end: 0.25, tickSize: 0.001, type: 'CLOSE_OPEN' },
      { begin: 0.25, end: 0.5, tickSize: 0.005, type: 'CLOSE_OPEN' },
      { begin: 0.5, end: 10, tickSize: 0.01, type: 'CLOSE_OPEN' },
      { begin: 10, end: 20, tickSize: 0.02, type: 'CLOSE_OPEN' },
      { begin: 20, end: 100, tickSize: 0.05, type: 'CLOSE_OPEN' },
      { begin: 100, end: 200, tickSize: 0.1, type: 'CLOSE_OPEN' },
      { begin: 200, end: 500, tickSize: 0.2, type: 'CLOSE_OPEN' },
      { begin: 500, end: 1000, tickSize: 0.5, type: 'CLOSE_OPEN' },
      { begin: 1000, end: 2000, tickSize: 1, type: 'CLOSE_OPEN' },
      { begin: 2000, end: 5000, tickSize: 2, type: 'CLOSE_OPEN' },
      { begin: 5000, end: +Infinity, tickSize: 5, type: 'CLOSE_OPEN' }
    ]
  }
  if (Array.isArray(minTick)) {
    return minTick.map(t => ({
      begin: t.begin,
      end: t.end,
      tickSize: t.minTick,
      type: 'CLOSED_OPEN'
    }))
  }
  // 期权固定
  if (stock.secType === SEC_TYPE.OPT) {
    return [
      {
        begin: 0,
        end: Infinity,
        tickSize: 0.01,
        type: 'CLOSED_OPEN'
      }
    ]
  }
  return [
    {
      begin: 0,
      end: 1,
      tickSize: 0.0001,
      type: 'CLOSED_OPEN'
    },
    {
      begin: 1,
      end: Infinity,
      tickSize: 0.01,
      type: 'CLOSED_OPEN'
    }
  ]
}

// 获取 ib 账户支持的订单类型
function getAvailableOrderTypes(stock) {
  if (stock.market === MARKET_TYPE.HK) {
    return [ORDER_TYPE.LMT, ORDER_TYPE.MKT, ORDER_TYPE.STP, ORDER_TYPE.STP_LMT]
  }
  return [
    ORDER_TYPE.LMT,
    ORDER_TYPE.MKT,
    ORDER_TYPE.STP,
    ORDER_TYPE.STP_LMT,
    ORDER_TYPE.TRAIL
  ]
}
